import React, { useEffect } from "react";
import { useTitle } from "../provider/TitleProvider";

const TermsOfUse = () => {
  const { setTitle } = useTitle();

  useEffect(() => {
    setTitle("Terms of Use");
  }, []);

  return (
    <div className="auto-container">
      <div className="row clearfix text-foreground">
        <p>
          These Terms of Use (<strong>Terms</strong>) govern your access to and use of the Retreevo platform, websites,
          mobile and web applications, and related services (collectively, the <strong>Services</strong>) operated by
          Retreevo, Inc. (<strong>Retreevo</strong>, <strong>we</strong>, <strong>us</strong> or <strong>our</strong>).
        </p>
        <p>
          By creating an account, accessing, or using the Services, you agree to be bound by these Terms and our{" "}
          <a href="/privacy-policy">Privacy Policy</a>. If you do not agree, you must not access or use the Services. If
          you are using the Services on behalf of an organization, you represent that you are authorized to bind that
          organization to these Terms, and <strong>you</strong> and <strong>your</strong> refer to that organization.
        </p>

        <h5>
          <strong>1. The Services</strong>
        </h5>
        <p>
          Retreevo provides a sales engagement and customer relationship management platform for real estate
          professionals, including power dialing, calling and SMS messaging, contact and list management, deal and
          property tracking, campaigns, automation, AI calling agents, and related tools. Calling and messaging
          functionality is delivered through third-party telecommunications providers. We may add, change, or remove
          features at any time.
        </p>

        <h5>
          <strong>2. Eligibility and Accounts</strong>
        </h5>
        <p>
          You must be at least 18 years of age and capable of forming a binding contract to use the Services. You agree
          to provide accurate, current, and complete information when you register and to keep it up to date.
        </p>
        <p>
          You are responsible for safeguarding your account credentials and for all activity that occurs under your
          account, including activity by users you invite to your organization. You must notify us promptly at{" "}
          <a href="mailto:support@retreevo.com">support@retreevo.com</a> if you suspect any unauthorized use of your
          account.
        </p>

        <h5>
          <strong>3. Telephony, Messaging, and Compliance Obligations</strong>
        </h5>
        <p>
          The Services let you place calls and send text messages to phone numbers you provide. You are solely
          responsible for ensuring that your use of these features complies with all applicable laws, rules, and
          industry standards, including but not limited to:
        </p>
        <ul>
          <li>the Telephone Consumer Protection Act (TCPA) and its implementing regulations;</li>
          <li>
            federal and state Do Not Call (DNC) requirements, including maintaining and honoring internal and registry
            DNC lists and applicable calling-time restrictions;
          </li>
          <li>the CAN-SPAM Act and applicable rules governing commercial text messaging;</li>
          <li>
            carrier and messaging-aggregator requirements, including 10DLC registration, opt-in and opt-out handling,
            and content restrictions; and
          </li>
          <li>all other applicable federal, state, and local laws governing telemarketing and consumer communications.</li>
        </ul>
        <p>
          You represent and warrant that you have obtained any consent legally required to contact each individual you
          reach through the Services, that you will promptly honor opt-out and revocation requests, and that you will not
          use the Services to transmit unlawful, deceptive, harassing, or unsolicited communications. Retreevo provides
          tools to help you manage consent and suppression (such as DNC and consent tracking features), but the use of
          those tools does not transfer responsibility for compliance to us. Compliance with these laws is your
          responsibility.
        </p>

        <h5>
          <strong>4. Acceptable Use</strong>
        </h5>
        <p>You agree not to, and not to permit any user or third party to:</p>
        <ul>
          <li>use the Services for any unlawful, fraudulent, or deceptive purpose;</li>
          <li>
            upload, store, or transmit any contact data you do not have the right to use, or any content that infringes
            the rights of others or violates the privacy of any individual;
          </li>
          <li>
            send spam or other unsolicited communications, or contact individuals who have opted out or whom you are not
            permitted to contact;
          </li>
          <li>
            interfere with, disrupt, probe, or attempt to gain unauthorized access to the Services or related systems or
            networks;
          </li>
          <li>
            reverse engineer, decompile, or attempt to derive the source code of the Services, except to the extent such
            restriction is prohibited by law;
          </li>
          <li>resell, sublicense, or make the Services available to third parties except as expressly permitted; or</li>
          <li>
            introduce viruses, worms, or other malicious code, or use the Services in any manner that could damage,
            disable, or impair the Services or interfere with any other party's use of them.
          </li>
        </ul>
        <p>
          We may investigate suspected violations and may suspend or terminate access, remove content, or take other
          action we deem appropriate, including reporting activity to law enforcement, without prior notice.
        </p>

        <h5>
          <strong>5. Your Data</strong>
        </h5>
        <p>
          You retain all rights in the contact records, lists, recordings, messages, and other data you submit to or
          generate through the Services (<strong>Customer Data</strong>). You grant Retreevo a worldwide, non-exclusive
          license to host, process, transmit, and display Customer Data as necessary to provide and improve the Services
          and as otherwise described in our <a href="/privacy-policy">Privacy Policy</a>. You are responsible for the
          accuracy, quality, and legality of Customer Data and for obtaining all rights and consents necessary for us to
          use it as contemplated by these Terms.
        </p>

        <h5>
          <strong>6. Fees, Billing, and Usage Charges</strong>
        </h5>
        <p>
          Paid plans, add-ons, and usage-based charges (such as per-minute calling, messaging, phone number, and AI
          agent usage) are billed through our third-party payment processor. By providing a payment method, you
          authorize us to charge all applicable fees, including recurring subscription fees and metered usage, to that
          method.
        </p>
        <p>
          Subscription fees are billed in advance and usage fees are billed in arrears or drawn from prepaid balances,
          as described at the time of purchase. Except as required by law or expressly stated, fees are non-refundable
          and amounts paid are not creditable. We may change our fees on a prospective basis by posting updated pricing
          or notifying you; continued use after a fee change constitutes acceptance. You are responsible for all taxes
          associated with your use of the Services other than taxes based on our net income.
        </p>
        <p>
          Fees do not include sales, use, or similar taxes. We calculate the taxes that apply from the billing address
          on your account or, if you have not provided one, from other location information we hold for you, such as
          your registered business address or the postal code of your payment method. Taxes are itemized on your
          monthly invoice and charged to your payment method together with your subscription fees. If you claim an
          exemption, you must provide a valid exemption certificate and keep it current, and you remain responsible for
          any tax, interest, or penalties if the exemption is denied. You agree to keep your billing address accurate.
        </p>

        <h5>
          <strong>7. Third-Party Services</strong>
        </h5>
        <p>
          The Services rely on and integrate with third-party providers, including telecommunications carriers, payment
          processors, and integration partners (such as CRM and data providers). Your use of those services may be
          subject to their own terms, and we are not responsible for the acts, omissions, availability, or content of
          third parties. Links and integrations are provided for convenience and do not constitute an endorsement.
        </p>

        <h5>
          <strong>8. Intellectual Property</strong>
        </h5>
        <p>
          The Services, including all software, text, graphics, logos, and other content provided by Retreevo, and all
          related intellectual property rights, are owned by Retreevo or its licensors. Subject to these Terms, we grant
          you a limited, non-exclusive, non-transferable, revocable license to access and use the Services for your
          internal business purposes. We reserve all rights not expressly granted. You may not use our names, logos, or
          trademarks without our prior written consent.
        </p>

        <h5>
          <strong>9. Disclaimers</strong>
        </h5>
        <p>
          The Services are provided on an <strong>as is</strong> and <strong>as available</strong> basis. To the
          maximum extent permitted by law, Retreevo disclaims all warranties, whether express, implied, statutory, or
          otherwise, including any implied warranties of merchantability, fitness for a particular purpose, title, and
          non-infringement. We do not warrant that the Services will be uninterrupted, secure, error-free, or that
          calls or messages will be delivered. Retreevo does not provide legal advice, and nothing in the Services
          constitutes legal advice regarding your compliance obligations.
        </p>

        <h5>
          <strong>10. Limitation of Liability</strong>
        </h5>
        <p>
          To the maximum extent permitted by law, in no event will Retreevo or its affiliates, officers, employees, or
          agents be liable for any indirect, incidental, special, consequential, exemplary, or punitive damages, or for
          any loss of profits, revenue, data, or goodwill, arising out of or relating to the Services or these Terms,
          whether based in contract, tort, or any other legal theory, even if advised of the possibility of such
          damages. Our total aggregate liability arising out of or relating to the Services or these Terms will not
          exceed the amounts you paid to us for the Services in the twelve months preceding the event giving rise to the
          claim.
        </p>

        <h5>
          <strong>11. Indemnification</strong>
        </h5>
        <p>
          You agree to defend, indemnify, and hold harmless Retreevo and its affiliates, officers, employees, and agents
          from and against any claims, damages, liabilities, losses, and expenses (including reasonable attorneys' fees)
          arising out of or related to your use of the Services, your Customer Data, your communications with third
          parties through the Services, or your violation of these Terms or applicable law, including any TCPA, DNC, or
          messaging-related claims arising from your use of the calling and messaging features.
        </p>

        <h5>
          <strong>12. Term, Suspension, and Termination</strong>
        </h5>
        <p>
          These Terms remain in effect while you use the Services. You may stop using the Services and close your
          account at any time. We may suspend or terminate your access at any time, with or without notice, if we
          reasonably believe you have violated these Terms, pose a risk to the Services or others, or fail to pay
          applicable fees. Upon termination, your right to use the Services ceases. Provisions that by their nature
          should survive, including those relating to Customer Data, fees owed, intellectual property, disclaimers,
          limitation of liability, indemnification, and governing law, will survive termination.
        </p>

        <h5>
          <strong>13. Changes to These Terms</strong>
        </h5>
        <p>
          We may modify these Terms from time to time. When we make material changes, we will update the date below and
          may provide additional notice. Your continued use of the Services after changes take effect constitutes
          acceptance of the revised Terms.
        </p>

        <h5>
          <strong>14. Governing Law</strong>
        </h5>
        <p>
          These Terms are governed by the laws of the State of Delaware, without regard to its conflict of laws
          principles. You agree to the exclusive jurisdiction of the state and federal courts located in Delaware for
          any dispute arising out of or relating to these Terms or the Services, except that we may seek injunctive
          relief in any court of competent jurisdiction.
        </p>

        <h5>
          <strong>15. Contact Us</strong>
        </h5>
        <p>
          If you have questions about these Terms, please contact us at{" "}
          <a href="mailto:support@retreevo.com">support@retreevo.com</a>.
        </p>

        <p>Last updated: September 25, 2026</p>
      </div>
    </div>
  );
};

export default TermsOfUse;
